import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import authService, {
  isMfaChallenge,
  type MfaChallengeResponse,
  type TokenResponse,
} from '../../services/auth.service'
import { useSignInProviders } from '../../hooks/useSignInProviders'
import { nextPathForToken } from '../../lib/roleRouting'
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError'
import { preformatRequestOptions, serializeAssertion } from '../../lib/webauthn'
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme'
import SignInProviderButtons from './SignInProviderButtons'
import { readChallengeHandoff, signInErrorCopy } from './signIn.helpers'

/** Friendly copy for the structured mfa_* error codes surfaced by
 *  /auth/mfa/verify. Falls back to the server message otherwise. */
function mfaErrorMessage(err: unknown): string {
  const code = getApiErrorCode(err)
  switch (code) {
    case 'mfa_invalid_code':
      return 'That code did not match. Enter the current code from your authenticator.'
    case 'mfa_code_replayed':
      return 'That code was already used. Wait for the next one and try again.'
    case 'mfa_too_many_attempts':
      return 'Too many attempts. Please wait a moment before trying again.'
    case 'mfa_challenge_invalid':
      // Not everyone reaches this step with a password — a sign-in through
      // another account lands here too — so the way back is not named.
      return 'This sign-in attempt expired. Please go back and sign in again.'
    case 'mfa_unavailable':
      return 'Two-factor verification is temporarily unavailable. Please try again shortly.'
    case 'webauthn_assertion_required':
      return 'Passkey verification did not complete. Try again or use a code.'
    default:
      return getApiErrorMessage(err, 'Could not verify the code. Please try again.')
  }
}

/** The banner a page opens with: an expired or secured session, or a failed sign-in through another account. */
function initialBannerFor(searchParams: URLSearchParams): string {
  const why = searchParams.get('reason')
  if (why === 'session_expired') {
    return 'Your session expired. Please sign in again to continue.'
  }
  if (why === 'inactive') {
    return 'You were signed out for inactivity. Please sign in again to continue.'
  }
  if (why === 'account_secured') {
    return 'Your account was secured and every session was signed out. Sign in again and choose a new password.'
  }
  const slug = searchParams.get('error')
  return slug ? signInErrorCopy(slug) : ''
}

/**
 * One button per sign-in option this environment offers, then the divider. With no options there
 * is nothing to divide, so the card goes straight to the email form. No box is ticked here: the
 * Terms and Conditions and the privacy notice are accepted after signing in (owner, 2026-10-28).
 */
function ProviderSignIn({
  providers,
  loading,
}: Readonly<{
  providers: ReturnType<typeof useSignInProviders>
  loading: boolean
}>): JSX.Element | null {
  if (providers.length === 0) return null
  return (
    <>
      <div className="mb-5">
        <SignInProviderButtons providers={providers} disabled={loading} />
      </div>

      {/* Divider */}
      <div className="relative mb-5">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full" style={{ borderTop: '1px solid #F0F0F0' }} />
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 bg-white text-xs text-gray-400">or continue with email</span>
        </div>
      </div>
    </>
  )
}

function PasswordToggle({ shown, onToggle }: Readonly<{ shown: boolean; onToggle: () => void }>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? 'Hide password' : 'Show password'}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
    >
      {shown ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  )
}

const Login = (): JSX.Element => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  // Two ways to land on /login with a pre-filled banner:
  //   ?error=...              — a sign-in through another account that
  //                              failed comes back here with a slug;
  //                              the banner shows a sentence, never
  //                              the slug (see signIn.helpers)
  //   ?reason=inactive        — the person was away for the whole
  //                              inactivity limit (useIdleSession)
  //   ?reason=session_expired — the axios interceptor sends users
  //                              here after a failed token refresh
  //                              (both tokens dead / admin-revoked
  //                              session / lockout). Friendly copy
  //                              instead of the raw error string.
  const [error, setError] = useState(() => initialBannerFor(searchParams))
  const [loading, setLoading] = useState(false)
  // Second-factor challenge for an already-enrolled user. Component-local
  // only — NEVER persisted (the mfa_token is single-use). When set, the
  // card swaps the email/password form for the second-factor step. It
  // starts set when /oauth/callback handed one over in navigation state:
  // a sign-in through another account whose own second factor is still owed.
  const [challenge, setChallenge] = useState<MfaChallengeResponse | null>(
    () => readChallengeHandoff(location.state),
  )
  const navigate = useNavigate()
  const providers = useSignInProviders()

  // A handed-over challenge lives in component state from here on. The
  // browser keeps navigation state with the history entry, so it is cleared
  // there: a reload or the Back button must not bring a spent challenge back.
  useEffect(() => {
    if (readChallengeHandoff(location.state) === null) return
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    )
  }, [location.state, location.pathname, location.search, navigate])

  // Fired once we hold a real Token (direct login OR after verifyMfa).
  // Hydrates the store from /auth/me and routes by the standard
  // precedence. `login()`/`verifyMfa()` already persisted the session,
  // with the account's address even when nobody typed one here (the
  // second-factor step reached through another account).
  const finishLogin = (token: TokenResponse): void => {
    // Screens that read first_name / last_name (Profile, UserMenu,
    // Dashboard subtitle) must not see the slim {email, role} stub the
    // login wrote.
    authService.hydrateSignedInUser()
    // Explicit redirect avoids the flicker the ProtectedRoute guard
    // would otherwise cause when a forced-rotation / enrolment flag is set.
    navigate(nextPathForToken(token), { replace: true })
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      // `authService.login()` persists the full session through the
      // single auth store when it returns a Token — no separate
      // setSession call needed here (post-2026-05-25 consolidation,
      // see authStore.ts). An enrolled user gets an MfaChallenge with
      // NO tokens; we switch to the second-factor step instead.
      const data = await authService.login({ email, password })
      if (isMfaChallenge(data)) {
        setChallenge(data)
        return
      }
      finishLogin(data)
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Authentication error. Please check your credentials.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
    >
      {/* ── Left branding panel ── */}
      <div className="hidden lg:flex lg:w-[58%] flex-col p-14 relative overflow-hidden">
        {/* Dot-grid texture */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px)`,
            backgroundSize: '36px 36px',
            pointerEvents: 'none',
          }}
        />
        {/* Gold glow blob */}
        <div
          style={{
            position: 'absolute',
            bottom: '-120px',
            left: '-80px',
            width: '520px',
            height: '520px',
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(212,168,75,0.12) 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm"
            style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`, color: NAVY }}
          >
            QMI
          </div>
          <span
            className="ml-1 text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ background: 'rgba(212,168,75,0.15)', color: GOLD, border: `1px solid rgba(212,168,75,0.3)` }}
          >
            Investor Portal
          </span>
        </div>

        {/* Main headline */}
        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-lg mt-16">
          <p
            className="text-xs font-semibold tracking-widest uppercase mb-5"
            style={{ color: GOLD }}
          >
            Quantitative  Investment Management
          </p>
          <h1 className="text-5xl font-extrabold text-white leading-[1.1] mb-6">
            Unlock the value<br />
            of your{' '}
            <span
              style={{
                background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              holdings
            </span>
          </h1>
          <p className="text-white/55 text-lg leading-relaxed mb-14">
            Secure access to your holdings, statements and investor services.
          </p>

          {/* Value props row — factual product capabilities only; never
              unverifiable firm claims (AUM, track record, investor counts) */}
          <div className="flex gap-10">
            {[
              { value: 'Private', label: 'Invitation-only access' },
              { value: 'Real-time', label: 'Holdings tracking' },
              { value: 'Encrypted', label: 'End-to-end data protection' },
            ].map((stat) => (
              <div key={stat.value}>
                <div className="text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-white/40 text-xs mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust badges */}
        <div className="relative z-10 flex items-center gap-3 flex-wrap">
          {[
            { icon: '🔒', label: 'AES-256 Encryption' },
            { icon: '🛡', label: 'Multi-factor authentication' },
          ].map((badge) => (
            <div
              key={badge.label}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              <span>{badge.icon}</span>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div
        className="flex-1 flex items-center justify-center p-8"
        style={{ background: 'rgba(255,255,255,0.025)', borderLeft: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-10 justify-center">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm"
              style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`, color: NAVY }}
            >
              Q
            </div>
            <span className="font-bold text-white text-lg">QMI Investor Portal</span>
          </div>

          {/* Card */}
          <div
            className="rounded-2xl p-9"
            style={{ background: '#FFFFFF', boxShadow: '0 32px 80px rgba(0,0,0,0.45)' }}
          >
            {challenge ? (
              <MfaStep
                challenge={challenge}
                email={email}
                onVerified={finishLogin}
                onCancel={() => { setChallenge(null); setError('') }}
              />
            ) : (
            <>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome</h2>
            <p className="text-gray-400 text-sm mb-7">Sign in to your investment portal</p>

            {/* Error */}
            {error && (
              <div className="mb-5 p-3 rounded-lg flex items-start gap-2 text-sm"
                style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <ProviderSignIn providers={providers} loading={loading} />

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="login-email" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                    style={{
                      background: '#F8F9FC',
                      border: '1.5px solid #E8ECF4',
                      color: '#111827',
                    }}
                    placeholder="name@company.com"
                    required
                    maxLength={254}
                    onFocus={e => (e.currentTarget.style.borderColor = GOLD)}
                    onBlur={e => (e.currentTarget.style.borderColor = '#E8ECF4')}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label htmlFor="login-password" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs font-medium transition-colors" style={{ color: GOLD }}>
                    Forgot your password?
                  </Link>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </span>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 text-sm rounded-xl outline-none transition-all"
                    style={{
                      background: '#F8F9FC',
                      border: '1.5px solid #E8ECF4',
                      color: '#111827',
                    }}
                    placeholder="••••••••"
                    required
                    maxLength={128}
                    disabled={loading}
                    onFocus={e => (e.currentTarget.style.borderColor = GOLD)}
                    onBlur={e => (e.currentTarget.style.borderColor = '#E8ECF4')}
                  />
                  <PasswordToggle shown={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-all mt-2"
                style={{
                  background: loading
                    ? '#E5E7EB'
                    : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
                  color: loading ? '#9CA3AF' : NAVY,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: loading ? 'none' : `0 6px 24px rgba(212,168,75,0.35)`,
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Lead-acquisition CTA (locked 2026-05-11) — strangers
                without an invitation are routed to the form rather
                than getting stuck on the login wall. */}
            <p className="mt-4 text-center text-sm text-gray-300">
              Don&apos;t have an account?{' '}
              <Link to="/auth/request-access" className="font-medium transition-colors" style={{ color: GOLD }}>
                Request access
              </Link>
            </p>

            {/* Footer — the Terms and Conditions in force, public at /terms. A client accepts them
                after signing in, the first time and again whenever the text changes. */}
            <p className="mt-6 text-center text-xs text-gray-400">
              <Link to="/terms" className="underline underline-offset-2" style={{ color: GOLD }}>
                Terms and Conditions
              </Link>
            </p>
            </>
            )}
          </div>

          <p className="mt-6 text-center text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
            © {new Date().getFullYear()} Quantum Momentum Investments. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}

interface MfaStepProps {
  readonly challenge: MfaChallengeResponse
  readonly email: string
  readonly onVerified: (token: TokenResponse) => void
  readonly onCancel: () => void
}

/**
 * Second-factor step shown after an enrolled user submits their password.
 * TOTP code is the default; a passkey button and a recovery-code toggle
 * appear only when the challenge lists those methods.
 */
function MfaStep({ challenge, email, onVerified, onCancel }: MfaStepProps): JSX.Element {
  const hasWebauthn = challenge.methods.includes('webauthn')
  const hasRecovery = challenge.methods.includes('recovery_code')
  const [useRecovery, setUseRecovery] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const method = useRecovery ? 'recovery_code' : 'totp'
  // TOTP is a 6-8 digit numeric OTP; recovery codes are alphanumeric.
  const codeValid = useRecovery ? code.trim().length >= 8 : code.trim().length >= 6

  const submitCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!codeValid) return
    setBusy(true)
    setError('')
    try {
      const token = await authService.verifyMfa(
        { mfa_token: challenge.mfa_token, method, code: code.trim() },
        email,
      )
      onVerified(token)
    } catch (err: unknown) {
      setError(mfaErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const signInWithPasskey = async (): Promise<void> => {
    setBusy(true)
    setError('')
    try {
      const optionsJson = await authService.getWebAuthnLoginOptions(challenge.mfa_token)
      const publicKey = preformatRequestOptions(
        optionsJson as unknown as Parameters<typeof preformatRequestOptions>[0],
      )
      const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
      if (!credential) {
        setError('Passkey verification was cancelled.')
        return
      }
      const token = await authService.verifyMfa(
        {
          mfa_token: challenge.mfa_token,
          method: 'webauthn',
          assertion: serializeAssertion(credential),
        },
        email,
      )
      onVerified(token)
    } catch (err: unknown) {
      // A user-cancelled / timed-out ceremony throws a DOMException, not an
      // axios error — mfaErrorMessage falls through to a generic message.
      if (err instanceof DOMException) {
        setError('Passkey verification did not complete. Try again or use a code.')
      } else {
        setError(mfaErrorMessage(err))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1">Verify it&apos;s you</h2>
      <p className="text-gray-400 text-sm mb-7">
        {useRecovery
          ? 'Enter one of your saved recovery codes.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>

      {error && (
        <div className="mb-5 p-3 rounded-lg flex items-start gap-2 text-sm"
          style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={submitCode} className="space-y-4">
        <div>
          <label htmlFor="mfa-code" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            {useRecovery ? 'Recovery code' : 'Authentication code'}
          </label>
          <input
            id="mfa-code"
            type="text"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(
              useRecovery
                ? e.target.value.trim().slice(0, 32)
                : e.target.value.replace(/\D/g, '').slice(0, 8),
            )}
            className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all font-mono tracking-widest"
            style={{ background: '#F8F9FC', border: '1.5px solid #E8ECF4', color: '#111827' }}
            placeholder={useRecovery ? 'XXXX-XXXXXXXXXXXX' : '123456'}
            disabled={busy}
            onFocus={e => (e.currentTarget.style.borderColor = GOLD)}
            onBlur={e => (e.currentTarget.style.borderColor = '#E8ECF4')}
          />
        </div>

        <button
          type="submit"
          disabled={busy || !codeValid}
          className="w-full py-3 rounded-xl font-semibold text-sm transition-all"
          style={{
            background: busy || !codeValid ? '#E5E7EB' : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
            color: busy || !codeValid ? '#9CA3AF' : NAVY,
            cursor: busy || !codeValid ? 'not-allowed' : 'pointer',
            boxShadow: busy || !codeValid ? 'none' : `0 6px 24px rgba(212,168,75,0.35)`,
          }}
        >
          {busy ? 'Verifying...' : 'Verify'}
        </button>
      </form>

      {hasWebauthn && (
        <button
          type="button"
          onClick={() => { signInWithPasskey() }}
          disabled={busy}
          className="w-full mt-3 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-all"
          style={{ background: '#FAFAFA', border: '1.5px solid #E5E7EB', color: '#374151' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          Use a passkey
        </button>
      )}

      <div className="mt-5 flex flex-col items-center gap-2 text-sm">
        {hasRecovery && (
          <button
            type="button"
            onClick={() => { setUseRecovery((v) => !v); setCode(''); setError('') }}
            disabled={busy}
            className="font-medium transition-colors"
            style={{ color: GOLD }}
          >
            {useRecovery ? 'Use your authenticator code instead' : 'Use a recovery code'}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          Back to sign in
        </button>
      </div>
    </div>
  )
}

export default Login
